import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  CreateTimesheetApprovalPayload,
  CreateTimesheetEntryPayload,
  CreateTimesheetPayload,
  HookParams,
  TimesheetApprovalListParams,
  TimesheetEntryListParams,
  TimesheetListParams,
  UpdateTimesheetApprovalPayload,
  UpdateTimesheetEntryPayload,
  UpdateTimesheetPayload,
} from '@/libs/interfaces'
import { timesheetApprovalsApi, timesheetEntriesApi, timesheetsApi } from '@/services/api'

export function useTimesheetsInfinite({ params, enabled }: HookParams<TimesheetListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.timesheets.infinite(params),
    queryFn: ({ pageParam }) => timesheetsApi.listCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev ?? undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheets({ params, enabled }: HookParams<TimesheetListParams>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheets.list(params),
    queryFn: () => timesheetsApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetByID({
  params,
  enabled,
}: HookParams<{ orgID: string; timesheetID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheets.id(params.timesheetID),
    queryFn: () => timesheetsApi.getByID(params.orgID, params.timesheetID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetFull({
  params,
  enabled,
}: HookParams<{ orgID: string; timesheetID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheets.full(params.timesheetID),
    queryFn: () => timesheetsApi.getFull(params.orgID, params.timesheetID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: CreateTimesheetPayload }) =>
      timesheetsApi.create(orgID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      timesheetID,
      payload,
    }: {
      orgID: string
      timesheetID: string
      payload: UpdateTimesheetPayload
    }) => timesheetsApi.update(orgID, timesheetID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, timesheetID }: { orgID: string; timesheetID: string }) =>
      timesheetsApi.delete(orgID, timesheetID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetEntriesInfinite({
  params,
  enabled,
}: HookParams<TimesheetEntryListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.timesheetEntries.infinite(params),
    queryFn: ({ pageParam }) => timesheetEntriesApi.listCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev ?? undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetEntries({ params, enabled }: HookParams<TimesheetEntryListParams>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheetEntries.list(params),
    queryFn: () => timesheetEntriesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetEntryByID({
  params,
  enabled,
}: HookParams<{ orgID: string; timesheetEntryID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheetEntries.id(params.timesheetEntryID),
    queryFn: () => timesheetEntriesApi.getByID(params.orgID, params.timesheetEntryID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetEntryCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: CreateTimesheetEntryPayload }) =>
      timesheetEntriesApi.create(orgID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetEntries.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetEntryUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      timesheetEntryID,
      payload,
    }: {
      orgID: string
      timesheetEntryID: string
      payload: UpdateTimesheetEntryPayload
    }) => timesheetEntriesApi.update(orgID, timesheetEntryID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetEntries.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetEntryDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, timesheetEntryID }: { orgID: string; timesheetEntryID: string }) =>
      timesheetEntriesApi.delete(orgID, timesheetEntryID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetEntries.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

// ── Timesheet Approvals ───────────────────────────────────────────────────────

export function useTimesheetApprovalsInfinite({
  params,
  enabled,
}: HookParams<TimesheetApprovalListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.timesheetApprovals.infinite(params),
    queryFn: ({ pageParam }) => timesheetApprovalsApi.listCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev ?? undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetApprovals({
  params,
  enabled,
}: HookParams<TimesheetApprovalListParams>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheetApprovals.list(params),
    queryFn: () => timesheetApprovalsApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetApprovalByID({
  params,
  enabled,
}: HookParams<{ orgID: string; timesheetApprovalID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.timesheetApprovals.id(params.timesheetApprovalID),
    queryFn: () => timesheetApprovalsApi.getByID(params.orgID, params.timesheetApprovalID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useTimesheetApprovalCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: CreateTimesheetApprovalPayload }) =>
      timesheetApprovalsApi.create(orgID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetApprovals.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetApprovalUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      timesheetApprovalID,
      payload,
    }: {
      orgID: string
      timesheetApprovalID: string
      payload: UpdateTimesheetApprovalPayload
    }) => timesheetApprovalsApi.update(orgID, timesheetApprovalID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetApprovals.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useTimesheetApprovalDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, timesheetApprovalID }: { orgID: string; timesheetApprovalID: string }) =>
      timesheetApprovalsApi.delete(orgID, timesheetApprovalID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheetApprovals.all,
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.timesheets.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
